import { FC, useMemo } from 'react'
import { Control, Controller, useWatch } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import { Box, Card, CardContent, Checkbox, Divider, FormControlLabel, FormHelperText, MenuItem, Stack, TextField, Typography } from '@mui/material'

import { InputTranslator } from '../../../../components/InputTranslator/InputTranslator'
import { WysiwygEditorConfig } from '../../../../components/InputTranslator/InputTranslatorDialog/InputTranslatorDialog'
import { InternalImageUpload } from '../../../../components/InternalImageUpload/InternalImageUpload'
import { InputTranslatorType } from '../../../../types/InputTranslatorType'
import {
  EditorialCardFormValues,
  fromDateTimeLocalValue,
  isValidActionTarget,
  stickyEmailOptions,
  toDateTimeLocalValue,
  toTranslations,
} from '../../helpers/editorialCardHelpers'
import { EditorialCardActionType } from '../../types/EditorialCard'

// Resolves to the URL inserted into the content.
export type ContentImageUploader = (file: Blob, fileName: string) => Promise<string>

type EditorialCardFormProps = {
  control: Control<EditorialCardFormValues>
  uploadContentImage: ContentImageUploader
  disabled?: boolean
}

export const EditorialCardForm: FC<EditorialCardFormProps> = ({ control, uploadContentImage, disabled }) => {
  const { t } = useTranslation()
  const actionType = useWatch({ control, name: 'actionType' })

  const contentEditorConfig = useMemo<WysiwygEditorConfig>(
    () => ({
      plugins: ['link', 'lists', 'image', 'media', 'searchreplace', 'code', 'autoresize'],
      toolbar: 'undo redo removeformat | blocks blockquote | bold italic underline | bullist numlist | outdent indent | image link media | code',
      init: {
        block_formats: 'Paragraph=p;Heading 5=h5;Heading 6=h6',
        image_uploadtab: true,
        images_upload_handler: (blobInfo) => uploadContentImage(blobInfo.blob(), blobInfo.filename()),
      },
    }),
    [uploadContentImage]
  )

  return (
    <Card variant="outlined">
      <CardContent>
        <Stack spacing={3}>
          <Controller
            name="titles"
            control={control}
            rules={{ validate: (value) => !!value.en.trim() }}
            render={({ field, fieldState }) => (
              <Box>
                <InputTranslator
                  value={field.value}
                  onChange={(value) => field.onChange(toTranslations(value))}
                  dialogTitle={t('internal-marketing:editorial_cards_title')}
                  label={t('internal-marketing:editorial_cards_title')}
                  disabled={disabled}
                />
                {fieldState.error && <FormHelperText error>{t('internal-marketing:editorial_cards_title_required')}</FormHelperText>}
              </Box>
            )}
          />
          <Controller
            name="subtitles"
            control={control}
            render={({ field }) => (
              <InputTranslator
                value={field.value}
                onChange={(value) => field.onChange(toTranslations(value))}
                dialogTitle={t('internal-marketing:editorial_cards_subtitle')}
                label={t('internal-marketing:editorial_cards_subtitle')}
                disabled={disabled}
              />
            )}
          />
          <Controller
            name="contents"
            control={control}
            render={({ field }) => (
              <InputTranslator
                value={field.value}
                onChange={(value) => field.onChange(toTranslations(value))}
                inputType={InputTranslatorType.WYSIWYG}
                dialogTitle={t('internal-marketing:editorial_cards_content')}
                label={t('internal-marketing:editorial_cards_content')}
                wysiwygConfig={contentEditorConfig}
                disabled={disabled}
              />
            )}
          />
          <Box>
            <Typography variant="body2" fontWeight="bold" mb={1}>
              {t('internal-marketing:editorial_cards_header_image')}
            </Typography>
            {/* InternalImageUpload has no disabled prop; a disabled fieldset disables its file input and buttons natively. */}
            <Box
              component="fieldset"
              disabled={disabled}
              sx={{
                border: 0,
                m: 0,
                p: 0,
                minWidth: 0,
                ...(disabled && { opacity: (theme) => theme.palette.action.disabledOpacity, '& .MuiButtonBase-root': { pointerEvents: 'none' } }),
              }}
            >
              <Controller
                name="image"
                control={control}
                render={({ field }) => (
                  <InternalImageUpload
                    value={field.value?.url ?? ''}
                    onChange={(_url, file) => field.onChange(file ?? null)}
                    type="editorialImage"
                    label={t('internal-marketing:editorial_cards_header_image')}
                    previewVariant="contain"
                    previewSize={100}
                    removable
                  />
                )}
              />
            </Box>
            <FormHelperText>{t('internal-marketing:editorial_cards_header_image_help')}</FormHelperText>
          </Box>

          <Divider />

          <Controller
            name="actionType"
            control={control}
            render={({ field }) => (
              <TextField {...field} select size="small" label={t('internal-marketing:editorial_cards_call_to_action')} disabled={disabled}>
                <MenuItem value={EditorialCardActionType.None}>{t('internal-marketing:editorial_cards_action_none')}</MenuItem>
                <MenuItem value={EditorialCardActionType.OpenSaasPage}>{t('internal-marketing:editorial_cards_action_open_url')}</MenuItem>
              </TextField>
            )}
          />
          {actionType === EditorialCardActionType.OpenSaasPage && (
            <>
              <Controller
                name="actionTarget"
                control={control}
                rules={{ validate: (value, values) => values.actionType !== EditorialCardActionType.OpenSaasPage || isValidActionTarget(value) }}
                render={({ field, fieldState }) => (
                  <TextField
                    {...field}
                    size="small"
                    required
                    label={t('internal-marketing:editorial_cards_action_target')}
                    placeholder="https://app.gamerefinery.com/daily-insights"
                    error={!!fieldState.error}
                    helperText={fieldState.error ? t('internal-marketing:editorial_cards_action_target_invalid') : undefined}
                    disabled={disabled}
                  />
                )}
              />
              <Controller
                name="actionLabels"
                control={control}
                rules={{ validate: (value, values) => values.actionType !== EditorialCardActionType.OpenSaasPage || !!value.en.trim() }}
                render={({ field, fieldState }) => (
                  <Box>
                    <InputTranslator
                      value={field.value}
                      onChange={(value) => field.onChange(toTranslations(value))}
                      dialogTitle={t('internal-marketing:editorial_cards_action_label')}
                      label={t('internal-marketing:editorial_cards_action_label')}
                      disabled={disabled}
                    />
                    {fieldState.error && <FormHelperText error>{t('internal-marketing:editorial_cards_action_label_required')}</FormHelperText>}
                  </Box>
                )}
              />
            </>
          )}

          <Divider />

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap alignItems="center">
            <Controller
              name="published"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={disabled} />}
                  label={t('internal-marketing:editorial_cards_published')}
                />
              )}
            />
            <Controller
              name="sticky"
              control={control}
              render={({ field }) => (
                <FormControlLabel
                  control={<Checkbox checked={field.value} onChange={(event) => field.onChange(event.target.checked)} disabled={disabled} />}
                  label={t('internal-marketing:editorial_cards_sticky')}
                />
              )}
            />
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <Controller
              name="stickyEmail"
              control={control}
              render={({ field }) => (
                <TextField
                  select
                  size="small"
                  label={t('internal-marketing:editorial_cards_sticky_email')}
                  helperText={t('internal-marketing:editorial_cards_sticky_email_help')}
                  value={field.value}
                  onChange={(event) => field.onChange(Number(event.target.value))}
                  sx={{ minWidth: 220 }}
                  disabled={disabled}
                >
                  {stickyEmailOptions.map((option) => (
                    <MenuItem key={option.value} value={option.value}>
                      {t(option.labelKey)}
                    </MenuItem>
                  ))}
                </TextField>
              )}
            />
            <Controller
              name="publishedAt"
              control={control}
              rules={{ validate: (value) => Number.isFinite(value) }}
              render={({ field, fieldState }) => (
                <TextField
                  type="datetime-local"
                  size="small"
                  required
                  label={t('internal-marketing:editorial_cards_published_on')}
                  value={toDateTimeLocalValue(field.value)}
                  onChange={(event) => field.onChange(fromDateTimeLocalValue(event.target.value))}
                  error={!!fieldState.error}
                  helperText={
                    fieldState.error ? t('internal-marketing:editorial_cards_published_on_required') : t('internal-marketing:editorial_cards_published_on_help')
                  }
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 260 }}
                  disabled={disabled}
                />
              )}
            />
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  )
}
